import { forwardRef, useId } from "react";
import { TextInput, type TextInputProps } from "../TextInput";
import s from "./Field.module.css";

export interface FieldProps extends TextInputProps {
  label: string;
  helperText?: string;
  errorText?: string;
}

// Forwards to the inner input, like TextInput does — a field that owns the
// label but hides the element behind it cannot be focused by its caller,
// which is what an auto-focusing single-question step needs.
export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, helperText, errorText, id, ...inputProps },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const helperId = `${inputId}-helper`;
  const errorId = `${inputId}-error`;
  const invalid = Boolean(errorText);
  const describedBy = [errorText ? errorId : null, helperText ? helperId : null]
    .filter(Boolean)
    .join(" ") || undefined;

  return (
    <div className={s.root}>
      <label htmlFor={inputId} className={s.label}>
        {label}
      </label>
      <TextInput
        ref={ref}
        id={inputId}
        invalid={invalid}
        aria-describedby={describedBy}
        {...inputProps}
      />
      {errorText ? (
        <span id={errorId} className={s.error}>
          {errorText}
        </span>
      ) : helperText ? (
        <span id={helperId} className={s.helper}>
          {helperText}
        </span>
      ) : null}
    </div>
  );
});
